import { Button } from "@/components/core/buttons/button";
import { type UserSession, userSessionsQueryOptions } from "@/features/dashboard/api/queries/user-sessions-query";
import { userSessionsService } from "@/features/dashboard/api/services/user-sessions-service";
import { IconDeviceDesktop, IconDeviceMobile, IconRefresh, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";

export function ProfileSessions() {
	const queryClient = useQueryClient();
	const [isTerminatingAll, setIsTerminatingAll] = useState(false);

	const { data: sessionsData, isLoading, refetch } = useQuery(userSessionsQueryOptions);
	const sessions = sessionsData?.data || [];

	const handleRefresh = () => {
		refetch();
	};

	const terminateSessionMutation = useMutation({
		mutationFn: userSessionsService.terminateSession,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["user-sessions"] });
		},
	});

	const terminateOtherSessionsMutation = useMutation({
		mutationFn: userSessionsService.terminateOtherSessions,
		onSuccess: () => {
			setIsTerminatingAll(false);
			queryClient.invalidateQueries({ queryKey: ["user-sessions"] });
		},
	});

	const handleTerminateSession = (sessionId: number) => {
		terminateSessionMutation.mutate(sessionId);
	};

	const handleTerminateOtherSessions = () => {
		setIsTerminatingAll(true);
		terminateOtherSessionsMutation.mutate();
	};

	const getDeviceIcon = (userAgent?: string) => {
		if (!userAgent) return <IconDeviceDesktop size={20} />;

		const isMobile = /Mobile|Android|iPhone|iPad/i.test(userAgent);
		return isMobile ? <IconDeviceMobile size={20} /> : <IconDeviceDesktop size={20} />;
	};

	const getBrowserName = (userAgent?: string): string => {
		if (!userAgent) return "Unknown Browser";

		if (userAgent.includes("Chrome")) return "Chrome";
		if (userAgent.includes("Firefox")) return "Firefox";
		if (userAgent.includes("Safari")) return "Safari";
		if (userAgent.includes("Edge")) return "Edge";
		return "Unknown Browser";
	};

	const getDeviceType = (userAgent?: string): string => {
		if (!userAgent) return "Desktop";

		if (/Mobile|Android/i.test(userAgent)) return "Mobile";
		if (/iPad|Tablet/i.test(userAgent)) return "Tablet";
		return "Desktop";
	};

	if (isLoading) {
		return <div className="p-4 text-center">Loading active sessions...</div>;
	}

	const currentSession = sessions.find((session) => session.is_current);
	const otherSessions = sessions.filter((session) => !session.is_current);
	const activeSessions = otherSessions.filter((session) => !session.is_logged_out);
	const loggedOutSessions = otherSessions.filter((session) => session.is_logged_out);

	return (
		<div className="flex flex-col gap-6 mt-4">
			<div className="flex flex-row items-center justify-between">
				<div>
					<h3 className="text-lg font-semibold text-foreground-dark">Active Sessions</h3>
					<p className="text-sm text-foreground/70">Manage your active login sessions across all devices</p>
				</div>
				<div className="flex flex-row gap-2">
					<Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isLoading}>
						<IconRefresh size={16} className={isLoading ? "animate-spin" : ""} />
					</Button>
					{activeSessions.length > 0 && (
						<Button variant="outline" size="sm" onClick={handleTerminateOtherSessions} disabled={isTerminatingAll || terminateOtherSessionsMutation.isPending}>
							{isTerminatingAll || terminateOtherSessionsMutation.isPending ? "Terminating..." : "Terminate Other Sessions"}
						</Button>
					)}
				</div>
			</div>

			<div className="flex flex-col gap-4">
				{/* Current Session */}
				{currentSession && (
					<div className="flex flex-row items-center justify-between p-4 rounded-md border border-green-200 bg-green-50">
						<div className="flex flex-row items-center gap-3">
							<div className="text-green-600">{getDeviceIcon(currentSession.user_agent)}</div>
							<div className="flex flex-col">
								<div className="flex flex-row items-center gap-2">
									<span className="font-medium text-foreground-dark">
										{getBrowserName(currentSession.user_agent)} on {getDeviceType(currentSession.user_agent)}
									</span>
									<span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Current Session</span>
								</div>
								<div className="text-sm text-foreground/70">
									{currentSession.ip_address && <span>IP: {currentSession.ip_address} • </span>}
									{currentSession.last_used_at ? (
										<span>Last active {formatDistanceToNow(new Date(currentSession.last_used_at), { addSuffix: true })}</span>
									) : (
										<span>Active now</span>
									)}
								</div>
							</div>
						</div>
						<div className="text-sm text-foreground/50 italic">Use logout to end this session</div>
					</div>
				)}

				{/* Active Sessions */}
				{activeSessions.length > 0 && (
					<>
						<h4 className="text-md font-medium text-foreground-dark">Other Active Sessions</h4>
						{activeSessions.map((session) => (
							<div key={session.id} className="flex flex-row items-center justify-between p-4 rounded-md border border-border/50">
								<div className="flex flex-row items-center gap-3">
									<div className="text-foreground/60">{getDeviceIcon(session.user_agent)}</div>
									<div className="flex flex-col">
										<span className="font-medium text-foreground-dark">
											{getBrowserName(session.user_agent)} on {getDeviceType(session.user_agent)}
										</span>
										<div className="text-sm text-foreground/70">
											{session.ip_address && <span>IP: {session.ip_address} • </span>}
											{session.last_used_at ? (
												<span>Last active {formatDistanceToNow(new Date(session.last_used_at), { addSuffix: true })}</span>
											) : (
												<span>Last active {formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}</span>
											)}
										</div>
									</div>
								</div>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => handleTerminateSession(session.id)}
									disabled={terminateSessionMutation.isPending}
									className="text-red-600 hover:text-red-700 hover:bg-red-50"
								>
									<IconTrash size={16} />
								</Button>
							</div>
						))}
					</>
				)}

				{/* Logged Out Sessions */}
				{loggedOutSessions.length > 0 && (
					<>
						<h4 className="text-md font-medium text-foreground-dark">Recent Logged Out Sessions</h4>
						{loggedOutSessions.map((session) => (
							<div key={session.id} className="flex flex-row items-center justify-between p-4 rounded-md border border-border/50 bg-gray-50 opacity-75">
								<div className="flex flex-row items-center gap-3">
									<div className="text-foreground/40">{getDeviceIcon(session.user_agent)}</div>
									<div className="flex flex-col">
										<div className="flex flex-row items-center gap-2">
											<span className="font-medium text-foreground/60">
												{getBrowserName(session.user_agent)} on {getDeviceType(session.user_agent)}
											</span>
											<span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded-full">Logged Out</span>
										</div>
										<div className="text-sm text-foreground/50">
											{session.ip_address && <span>IP: {session.ip_address} • </span>}
											{session.logged_out_at && <span>Logged out {formatDistanceToNow(new Date(session.logged_out_at), { addSuffix: true })}</span>}
										</div>
									</div>
								</div>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => handleTerminateSession(session.id)}
									disabled={terminateSessionMutation.isPending}
									className="text-gray-400 hover:text-gray-500 hover:bg-gray-100"
								>
									<IconTrash size={16} />
								</Button>
							</div>
						))}
					</>
				)}

				{sessions.length === 0 && (
					<div className="p-8 text-center text-foreground/70">
						<IconDeviceDesktop size={48} className="mx-auto mb-2 opacity-50" />
						<p>No active sessions found</p>
					</div>
				)}
			</div>
		</div>
	);
}
